import {
  App,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from 'dayjs';
import { notificationsApi, type SmsMessage, type SmsStatus } from '@/api/notifications';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

type Filter = 'ALL' | SmsStatus;

const STATUS_COLOR: Record<SmsStatus, string> = {
  QUEUED: 'default',
  SENDING: 'processing',
  SENT: 'success',
  FAILED: 'error',
};

interface EnqueueForm {
  phoneNumber: string;
  body: string;
  sourceType?: string;
  dedupeKey?: string;
}

export function SmsQueuePage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('NOTIFICATIONS:CREATE'));

  const [filter, setFilter] = useState<Filter>('ALL');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [detail, setDetail] = useState<SmsMessage | null>(null);
  const [enqueueOpen, setEnqueueOpen] = useState(false);
  const [form] = Form.useForm<EnqueueForm>();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['notifications', 'sms', filter, page, pageSize],
    queryFn: () =>
      notificationsApi.list({
        status: filter === 'ALL' ? undefined : filter,
        page,
        size: pageSize,
      }),
    refetchInterval: 5_000,
  });

  const enqueue = useMutation({
    mutationFn: (values: EnqueueForm) =>
      notificationsApi.enqueue({
        phoneNumber: values.phoneNumber.trim(),
        body: values.body,
        sourceType: values.sourceType?.trim() || undefined,
        dedupeKey: values.dedupeKey?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Message queued');
      qc.invalidateQueries({ queryKey: ['notifications', 'sms'] });
      setEnqueueOpen(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const columns: TableProps<SmsMessage>['columns'] = [
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: SmsStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      width: 150,
      render: (p: string) => <span style={{ fontFamily: 'monospace' }}>{p}</span>,
    },
    {
      title: 'Body',
      dataIndex: 'body',
      ellipsis: true,
      render: (b: string) => (
        <Tooltip title={b} placement="topLeft">
          <Typography.Text>{b}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'sourceType',
      width: 160,
      render: (t: string | null, row) =>
        t ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t}
            {row.sourceId != null ? ` · ${row.sourceId}` : ''}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Typography.Text>
        ),
    },
    {
      title: 'Attempts',
      dataIndex: 'attempts',
      width: 90,
      align: 'center',
      render: (n: number, row) => `${n} / ${row.maxAttempts}`,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => dayjs(v).format('D MMM HH:mm:ss'),
    },
    {
      title: 'Sent',
      dataIndex: 'sentAt',
      width: 160,
      render: (v: string | null) => (v ? dayjs(v).format('D MMM HH:mm:ss') : '—'),
    },
    {
      title: '',
      width: 80,
      align: 'right',
      render: (_, row) => (
        <Button size="small" type="link" onClick={() => setDetail(row)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Notifications"
        title="SMS queue"
        description="Outbound SMS waiting for the gateway phone. Auto-refreshes every 5 seconds."
        count={{ value: data?.totalElements ?? 0, label: 'messages' }}
        actions={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refresh
            </Button>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setEnqueueOpen(true)}>
                Send test SMS
              </Button>
            )}
          </Space>
        }
      />

      <Segmented<Filter>
        value={filter}
        onChange={(v) => {
          setFilter(v);
          setPage(0);
        }}
        options={[
          { label: 'All', value: 'ALL' },
          { label: 'Queued', value: 'QUEUED' },
          { label: 'Sending', value: 'SENDING' },
          { label: 'Sent', value: 'SENT' },
          { label: 'Failed', value: 'FAILED' },
        ]}
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data?.content ?? []}
        pagination={{
          current: (data?.number ?? 0) + 1,
          pageSize: data?.size ?? pageSize,
          total: data?.totalElements ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 200],
          onChange: (p, ps) => {
            setPage(p - 1);
            setPageSize(ps);
          },
        }}
      />

      <Drawer
        title={detail ? `SMS · #${detail.id}` : ''}
        open={!!detail}
        onClose={() => setDetail(null)}
        size={520}
        destroyOnHidden
      >
        {detail && (
          <div style={{ display: 'grid', gap: 14 }}>
            <Field label="Status">
              <Tag color={STATUS_COLOR[detail.status]}>{detail.status}</Tag>
            </Field>
            <Field label="Phone">
              <span style={{ fontFamily: 'monospace' }}>{detail.phoneNumber}</span>
            </Field>
            <Field label="Body">
              <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                {detail.body}
              </Typography.Paragraph>
            </Field>
            <Field label="Attempts">
              {detail.attempts} / {detail.maxAttempts}
            </Field>
            <Field label="Source">
              {detail.sourceType ? `${detail.sourceType} · ${detail.sourceId ?? '—'}` : '—'}
            </Field>
            <Field label="Dedupe key">{detail.dedupeKey ?? '—'}</Field>
            <Field label="Claimed by">
              {detail.claimedByDeviceName ?? (detail.claimedByDeviceId ? `Device #${detail.claimedByDeviceId}` : '—')}
            </Field>
            <Field label="Created">{dayjs(detail.createdAt).format('D MMM YYYY HH:mm:ss')}</Field>
            <Field label="Claimed at">
              {detail.claimedAt ? dayjs(detail.claimedAt).format('D MMM YYYY HH:mm:ss') : '—'}
            </Field>
            <Field label="Sent at">
              {detail.sentAt ? dayjs(detail.sentAt).format('D MMM YYYY HH:mm:ss') : '—'}
            </Field>
            {detail.failedReason && (
              <Field label="Failed reason">
                <Typography.Text type="danger">{detail.failedReason}</Typography.Text>
              </Field>
            )}
          </div>
        )}
      </Drawer>

      <Modal
        title="Send test SMS"
        open={enqueueOpen}
        onCancel={() => setEnqueueOpen(false)}
        onOk={() => form.submit()}
        okText="Queue"
        confirmLoading={enqueue.isPending}
        destroyOnHidden
      >
        {enqueueOpen && (
          <Form form={form} layout="vertical" onFinish={(v) => enqueue.mutate(v)}>
            <Form.Item
              label="Phone number"
              name="phoneNumber"
              tooltip="Pakistani numbers are normalized to +92… on the server."
              rules={[{ required: true, max: 20 }]}
            >
              <Input placeholder="03001234567" autoFocus />
            </Form.Item>
            <Form.Item
              label="Message"
              name="body"
              rules={[{ required: true, max: 1000 }]}
            >
              <Input.TextArea rows={4} placeholder="Type the SMS body…" showCount maxLength={1000} />
            </Form.Item>
            <Form.Item label="Source tag (optional)" name="sourceType">
              <Input placeholder="e.g. TEST, FEE_REMINDER" maxLength={40} />
            </Form.Item>
            <Form.Item
              label="Dedupe key (optional)"
              name="dedupeKey"
              tooltip="If set, the same key won't be queued twice — useful for idempotent reminders."
            >
              <Input placeholder="e.g. test:2026-05-21-001" maxLength={120} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
        {label}
      </Typography.Text>
      <div>{children}</div>
    </div>
  );
}
